"use client";

import { useEffect, useId } from "react";
import { AlertTriangle, X, Loader2 } from "lucide-react";

// Generic confirmation modal used by the Developers panel for key
// rotation/revocation. Replaces window.confirm() so we can render
// branded copy and let the user weigh the action with proper visual
// emphasis (variant="danger" for destructive operations).

export interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "danger";
  busy?: boolean;
  icon?: React.ReactNode;
}

export default function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  busy = false,
  icon,
}: ConfirmModalProps) {
  const titleId = useId();
  const descriptionId = useId();

  // Esc-to-close + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", handler);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, busy]);

  if (!open) return null;

  const accent =
    variant === "danger"
      ? { btn: "bg-red-500 hover:bg-red-600 text-white", ring: "border-red-500/30", tile: "bg-red-500/10 border-red-500/25 text-red-400" }
      : { btn: "bg-[#61CE70] hover:bg-[#50B060] text-black", ring: "border-[#61CE70]/30", tile: "bg-[#61CE70]/10 border-[#61CE70]/25 text-[#61CE70]" };

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-[fadeIn_120ms_ease-out]"
      onClick={() => !busy && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={`relative w-full max-w-md rounded-2xl border ${accent.ring} bg-gradient-to-br from-[#0F1419] to-[#0B0F16] shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          disabled={busy}
          aria-label="Close"
          className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-40"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 sm:p-7">
          <div className="flex items-start gap-3">
            <div className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 ${accent.tile}`}>
              {icon || <AlertTriangle className="w-5 h-5" />}
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <h2 id={titleId} className="text-white font-bold text-lg leading-snug">{title}</h2>
              {description && (
                <p id={descriptionId} className="mt-1.5 text-gray-400 text-sm leading-relaxed">
                  {description}
                </p>
              )}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 rounded-xl text-gray-300 hover:bg-white/5 text-sm font-medium transition-colors disabled:opacity-40"
            >
              {cancelLabel}
            </button>
            <button
              onClick={() => onConfirm()}
              disabled={busy}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60 ${accent.btn}`}
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
      <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
    </div>
  );
}
